import { useEffect } from "react";
import { useHistory } from "react-router-dom";
import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";

import { handleNativeBack } from "../../../helpers/nativeBackHandler";
import URL_PATTERNS from "../../../urls";
import { isLoggedIn } from "../../../helpers/auth";

/**
 * Top-level wiring for the Android hardware back button.
 *
 * Order of resolution on each press:
 *   1. Any registered handler (e.g. an open bottom sheet) returns true →
 *      consumed.
 *   2. There's somewhere to go back to in the history stack → pop it.
 *   3. We're on a tab root (dashboard/login) → exit the app, like every
 *      other native app.
 *
 * Web builds and iOS get nothing — this is hardware-back specific.
 */
const NativeHardwareBack = () => {
  const history = useHistory();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;

    const subscriptionPromise = App.addListener("backButton", ({ canGoBack }) => {
      if (handleNativeBack()) return;

      const home = isLoggedIn() ? URL_PATTERNS.MY_DASHBOARD : URL_PATTERNS.WELCOME;
      // Treat the home/welcome root as the "exit point" — on those routes,
      // back closes the app rather than popping into stale auth screens.
      if (history.location.pathname === home || !canGoBack) {
        App.exitApp();
        return;
      }
      history.goBack();
    });

    return () => {
      subscriptionPromise.then((sub) => sub.remove()).catch(() => undefined);
    };
  }, [history]);

  return null;
};

export default NativeHardwareBack;
